import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { WelcomeModal } from "./WelcomeModal";
import { TutorialTour } from "./TutorialTour";
import { useTutorialProgress } from "./useTutorialProgress";
import { createGa4EventEmitter } from "./emitGa4";
import EnvironmentUtils from "utils/EnviromentUtils";

// 2026-07-22 の本番事故と、その直し方について。
//
// 事故: GET /api/staff/tutorial/progress が本番で 404 だった。
//       hook が「取れなかった → 空の状態」で返し、空の状態は
//       finishedAt が null なので isFresh = true になり、
//       全 clientMaster にウェルカムが出た。
//       応急処置として、この関数を return null にして mount ごと止めた。
//
// 直し方: 止めるのをやめて、hook 側で 404 を「機能が無い」と判定させた。
//         404 のときは unavailable = true になり、progress は
//         finishedAt 入りの状態になるので、下の isFresh が false になる。
//         つまり **サーバが未 deploy でも、何も出ない。**
//
//         404 以外 (通信断・500) は一時的なものとして扱い、
//         キャッシュされた状態を維持する (毎回の一時障害で消えないように)。
export function TutorialWrapper() {
  // フックなので early return より前に置く。
  // 子は "tutorial.welcome.title" の形で渡してくるので、ns 名の接頭辞を外して
  // tutorial ns の相対キーにする（i18n/index.ts で ns に登録済み）。
  const { t } = useTranslation("tutorial");
  const translate = useCallback(
    (key: string, fallback: string) =>
      t(key.replace(/^tutorial\./, ""), fallback),
    [t]
  );

  const isLoggedIn = useSelector((state: any) =>
    Boolean(state.account?.accessToken)
  );
  const staff = useSelector((state: any) => state.account?.staff);

  // REACT_APP_API_BASE already includes /v1/api/staff; the hook prepends
  // /api/staff/tutorial/progress, so strip the /api/staff tail here to land
  // on /v1/api/staff/tutorial/progress.
  const apiBaseUrl = useMemo(
    () => EnvironmentUtils.getApiUrl().replace(/\/api\/staff\/?$/, ""),
    []
  );

  // ★ token を渡さないと 401 になる。
  //   このアプリの他の API は utils/RequestUtils が fetch() で
  //   Authorization: Bearer を付けている。axios の interceptor は無いので、
  //   生の axios を使うこの hook には自分で渡す必要がある。
  //   (2026-07-28 QA 実測: 他の API 200 / ここだけ 401 だった)
  const accessToken = useSelector((state: any) => state.account?.accessToken);

  const { progress, loading, unavailable, error } = useTutorialProgress({
    apiBaseUrl,
    enableRemote: isLoggedIn,
    accessToken,
  });

  // 2026-08-26 …計測が 1 件も取れていなかった。
  //   emitGa4.ts は 2026-07-20 に入っていたが、呼んでいる所が 0 だった。
   //   TutorialTour は onEvent を受け取る作りで、渡していなかったので
  //   既定の noop に流れていた（「作った ≠ 繋がっている」）。
  //   札は usePageTracking.ts が initialize している物をそのまま使う
  //   （ここでは initialize しない。二重に initialize しないため）。
  const emitGa4 = useMemo(() => createGa4EventEmitter(), []);

  const [tourStarted, setTourStarted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleStart = useCallback(() => {
    setTourStarted(true);
    setDismissed(true);
  }, []);
  const handleSkip = useCallback(() => setDismissed(true), []);

  if (!isLoggedIn) return null;
  if (loading) return null;
  // サーバにこの機能がまだ無い → 何も出さない。
  // (progress.finishedAt でも同じ結果になるが、意図が読めるよう明示する)
  if (unavailable) return null;
  // ★ 状態が確認できていないなら、自動では出さない。
  //
  //   404 だけを見ていると、401 や 500 や通信断のときに
  //   「まだ何もしていない状態」と読んで全員にウェルカムが出る。
  //   2026-07-22 の事故は 404 が引き金だったが、原因は
  //   ★取れなかったことを、まだ何もしていないことと同じに扱った★ことなので、
  //   引き金の種類を数え上げるのではなく、確認できたときだけ出す形にする。
  //
  //   キャッシュは消さない。次に取れたときに続きから再開できる。
  if (error) return null;
  // Gate: fresh state only (no completed steps, not finished)
  const isFresh =
    progress.completedStepIds.length === 0 && !progress.finishedAt;
  if (!isFresh) return null;
  // Role gate: clientMaster only (matches W2 requiredRole default)
  if (staff?.role && staff.role !== "clientMaster") return null;

  return (
    <>
      <WelcomeModal
        open={!dismissed}
        onStart={handleStart}
        onSkip={handleSkip}
        translate={translate}
      />
      {/* ★translate を 渡さないと useTutorial の defaultTranslate が
          fallback（日本語）を そのまま 返す★。
          841872c4 は WelcomeModal にだけ 渡していたので、
          手順の中身（16 本）は 英語 UI でも 日本語のままだった（2026-08-26 実測）*/}
      {tourStarted && (
        <TutorialTour autoStart onEvent={emitGa4} translate={translate} />
      )}
    </>
  );
}
